import type { ReactNode } from "react";

import { Cloudinary } from "@cloudinary/url-gen";
import { scale } from "@cloudinary/url-gen/actions/resize";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Image from "next/image";

import composeIssueTitle from "../../functions/composeIssueTitle";
import { rewriteLegacyLinks } from "../../functions/rewriteLegacyLinks";
import type { GetArticleByIdQuery } from "../../gql/graphql";
import Breadcrumb from "../Breadcrumb";
import { ArticleBibleReferences } from "./ArticleBibleReferences";

// From: https://github.com/mayashavin/cloudinary-api/blob/f41d461e286a9d3230c0343ebe83828eb71319e8/packages/url/lib/url.ts#L9-L17
const CLOUDINARY_REGEX =
  /^.+\.cloudinary\.com\/(?:[^\/]+\/)(?:(image|video|raw)\/)?(?:(upload|fetch|private|authenticated|sprite|facebook|twitter|youtube|vimeo)\/)?(?:(?:[^_/]+_[^,/]+,?)*\/)?(?:v(\d+|\w{1,2})\/)?([^\.^\s]+)(?:\.(.+))?$/;

export const extractPublicId = (link: string): string => {
  if (!link) return "";

  const parts = CLOUDINARY_REGEX.exec(link);

  return parts && parts.length > 2 ? parts[parts.length - 2] : link;
};

interface ArticleContentProps {
  article: NonNullable<GetArticleByIdQuery["articles"]>;
  sidebar?: ReactNode;
}

export function ArticleContent({ article, sidebar }: ArticleContentProps) {
  const { title, teste, issue, image } = article;

  const thumbnailImage = image?.[0]?.secure_url;

  const content: string | undefined = teste?.json.content?.[0].content?.[0].value;

  if (!content || !title || !issue) {
    return null;
  }

  const regex = new RegExp(/src\=\"(https:\/\/res\.cloudinary\.com.+?)\"/g);

  const images = Array.from(content.matchAll(regex)).map((item) => item[1]);

  const cld = new Cloudinary({
    cloud: {
      cloudName: "dstgp4faa",
    },
  });

  const contentWithReplacedImages = images.reduce((updatedContent, imageUrl) => {
    const myImage = cld.image(extractPublicId(imageUrl));

    const newImageUrl = myImage.resize(scale().width(700)).format("jpg").toURL();

    return updatedContent.replace(imageUrl, newImageUrl);
  }, rewriteLegacyLinks(content));

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={9}>
        <Box display="flex" justifyContent="center" flexWrap="wrap">
          <Breadcrumb
            sx={{ marginBottom: 4 }}
            items={[
              { label: "Início", href: "/" },
              ...(issue.isPreRelaunch
                ? [
                    { label: "Edições Antigas", href: "/edicoes#edicoes-antigas" },
                    { label: composeIssueTitle(issue), href: `/edicoes-antigas/${issue.name}` },
                  ]
                : [
                    { label: "Edições", href: "/edicoes" },
                    { label: composeIssueTitle(issue), href: `/edicao/${issue.yearMonth?.split("T")[0]}` },
                  ]),
              { label: title },
            ]}
          />

          {thumbnailImage && (
            <Box sx={{ width: "100%", height: "500px", position: "relative", marginBottom: 2 }}>
              <Image
                layout="fill"
                objectFit="cover"
                objectPosition="center center"
                src={thumbnailImage}
                alt={title ?? ""}
              />
            </Box>
          )}

          <Typography sx={{ flex: "1 1 100%", maxWidth: 700 }} variant="h1" gutterBottom>
            {title}
          </Typography>

          <ArticleBibleReferences>
            <Box
              sx={{
                maxWidth: 700,
                fontFamily: "'Noto Sans JP', sans-serif",
                lineHeight: 1.9,
                fontSize: "1rem",
                "& > blockquote": {
                  fontSize: "1.3rem",
                  borderLeft: "4px solid #e6e6e6",
                  paddingLeft: 3,
                  color: grey[700],
                },
              }}
              className="ck-content"
              dangerouslySetInnerHTML={{ __html: contentWithReplacedImages }}
            />
          </ArticleBibleReferences>
        </Box>
      </Grid>

      {sidebar ? (
        <Grid item xs={12} md={3}>
          {sidebar}
        </Grid>
      ) : null}
    </Grid>
  );
}
